if (!customElements.get('option-toggle')) {
  customElements.define(
    'option-toggle',
    class OptionToggle extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.option-toggle__input');
        this.form = document.getElementById(this.dataset.formId);
        if (!this.input || !this.form) return;

        this.priceContainer = document.getElementById(this.dataset.priceTarget);
        this.prices = this.parsePrices();

        this.fields = {
          mainId: this.querySelector('[data-option-toggle-main-id]'),
          mainQuantity: this.querySelector('[data-option-toggle-main-quantity]'),
          optionId: this.querySelector('[data-option-toggle-option-id]'),
          optionQuantity: this.querySelector('[data-option-toggle-option-quantity]'),
        };

        // Le formulaire des cartes cadeaux se repere a l'identifiant de
        // variante renvoye par product-form : on le laisse intact.
        this.canAddOption =
          !this.form.querySelector('recipient-form') && Object.values(this.fields).every((field) => field);

        this.input.addEventListener('change', this.updatePrice.bind(this));
        this.bindModalButton();

        // Le prix affiche suit la quantite saisie, pour qu'il corresponde a ce
        // qui sera reellement ajoute au panier.
        const quantityInput = this.quantityInput;
        if (quantityInput) {
          quantityInput.addEventListener('change', this.updatePrice.bind(this));
          quantityInput.addEventListener('input', this.updatePrice.bind(this));
        }

        // Phase de capture : les champs sont a jour avant que product-form
        // ne serialise le formulaire.
        this.form.addEventListener('submit', this.syncCartFields.bind(this), { capture: true });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this.updatePrice();
        });

        this.updatePrice();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      // Le bouton de la fenetre modale active l'option puis referme la
      // fenetre. La modale est deplacee dans le body a l'ouverture : on la
      // retrouve par son identifiant.
      bindModalButton() {
        const modal = this.dataset.modalId ? document.getElementById(this.dataset.modalId) : null;
        const button = modal ? modal.querySelector('[data-option-toggle-add]') : null;
        if (!button) return;

        button.addEventListener('click', () => {
          this.input.checked = true;
          this.updatePrice();
          if (typeof modal.hide === 'function') modal.hide();
        });
      }

      parsePrices() {
        const script = this.querySelector('.option-toggle__prices');
        if (!script) return {};

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return {};
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name="id"]');
      }

      get quantityInput() {
        return this.form.querySelector('[name="quantity"]');
      }

      get quantity() {
        const input = this.quantityInput;
        const value = input ? parseInt(input.value, 10) : 1;
        return Number.isNaN(value) || value < 1 ? 1 : value;
      }

      updatePrice() {
        if (!this.priceContainer) return;

        const variantIdInput = this.variantIdInput;
        const entry = variantIdInput ? this.prices[variantIdInput.value] : null;
        if (!entry) return;

        const active = this.input.checked;
        const quantity = this.quantity;

        let price;
        let compareAtPrice;

        if (quantity === 1) {
          // Chaines rendues par Liquid : exactes, quel que soit le format
          // monetaire ou la devise de la boutique.
          price = active ? entry.priceWithOption : entry.price;
          compareAtPrice = active ? entry.compareAtPriceWithOption : entry.compareAtPrice;
        } else {
          // L'option n'est ajoutee qu'une fois, quelle que soit la quantite :
          // elle n'est donc pas multipliee ici non plus.
          const optionCents = active ? parseInt(this.dataset.optionPrice, 10) || 0 : 0;
          price = this.formatMoney(entry.priceCents * quantity + optionCents);
          compareAtPrice =
            entry.compareAtPriceCents === null
              ? null
              : this.formatMoney(entry.compareAtPriceCents * quantity + optionCents);
        }

        this.setPriceText('.price__regular .price-item--regular', price);
        this.setPriceText('.price__sale .price-item--sale', price);
        if (compareAtPrice) this.setPriceText('.price__sale s.price-item--regular', compareAtPrice);
      }

      /*
       * Reprend le formatage de Shopify a partir du money_format de la
       * boutique, pour que les totaux calcules ici s'ecrivent exactement comme
       * ceux rendus par Liquid.
       */
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return format;

        const withDelimiters = (precision, thousands, decimal) => {
          const amount = (cents / 100).toFixed(precision);
          const parts = amount.split('.');
          const units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? units + decimal + parts[1] : units;
        };

        const formats = {
          amount: () => withDelimiters(2, ',', '.'),
          amount_no_decimals: () => withDelimiters(0, ',', '.'),
          amount_with_comma_separator: () => withDelimiters(2, '.', ','),
          amount_with_space_separator: () => withDelimiters(2, ' ', ','),
          amount_with_period_and_space_separator: () => withDelimiters(2, ' ', '.'),
          amount_with_apostrophe_separator: () => withDelimiters(2, "'", '.'),
          amount_no_decimals_with_comma_separator: () => withDelimiters(0, '.', ','),
          amount_no_decimals_with_space_separator: () => withDelimiters(0, ' ', ','),
        };

        const build = formats[match[1]] || formats.amount;
        return format.replace(placeholder, build());
      }

      setPriceText(selector, value) {
        this.priceContainer.querySelectorAll(selector).forEach((element) => {
          element.textContent = value;
        });
      }

      syncCartFields() {
        const active = this.canAddOption && this.input.checked;

        Object.values(this.fields).forEach((field) => {
          if (field) field.disabled = !active;
        });

        if (!active) return;

        const variantIdInput = this.variantIdInput;
        const quantityInput = this.quantityInput;

        this.fields.mainId.value = variantIdInput ? variantIdInput.value : '';
        this.fields.mainQuantity.value = quantityInput ? quantityInput.value : 1;
        this.fields.optionQuantity.value = 1;

        // « id » et « quantity » feraient double emploi avec « items[] ».
        if (variantIdInput) variantIdInput.disabled = true;
        if (quantityInput) quantityInput.disabled = true;

        // Le reste du theme lit ces champs : on les remet en service des que
        // product-form a serialise le formulaire.
        setTimeout(() => {
          if (variantIdInput) variantIdInput.disabled = false;
          if (quantityInput) quantityInput.disabled = false;
        });
      }
    }
  );
}
